import React from "react";
import { ObjectCardItem } from "src/components/ui/ObjectCard";
import { Box, Divider, Group, Paper, SimpleGrid, Stack } from "@mantine/core";
import { ViewSchemaType, ViewSchemaFieldItem } from "src/state/_types";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Icons } from "src/components/ui/Icons";

type ViewSchemaRendererProps<T extends Record<string, any>> = {
    schema: ViewSchemaType<T>;
    data: T;
    title?: string;
    icon?: string;
    fieldColumns?: number;
    relationColumns?: number;
};

export function ViewSchemaRenderer<T extends Record<string, any>>({
    schema,
    data,
    title = "Overview",
    icon = Icons.point,
    fieldColumns = 4,
    relationColumns = 3,
}: ViewSchemaRendererProps<T>) {

    return (
        <Stack>

            <Paper bg="white" p="md" shadow="xs">

                <Divider
                    my="0"
                    mb="xs"
                    variant="dashed"
                    labelPosition="left"
                    color="var(--mantine-color-gray-4)"
                    styles={{
                        label: {
                            color: 'var(--mantine-color-gray-7)',
                            fontWeight: 400,
                            fontSize: '16px',
                        }
                    }}
                    label={
                        <Group gap="3" align="center" justify="flex-start">
                            <Icon icon={icon} width={20} />
                            <Box ml={5}>{title}</Box>
                        </Group>
                    }
                />

                <Box p="8px">

                    <SimpleGrid cols={{ base: 1, md: fieldColumns }} spacing="xs">

                        {schema.enums.map((item) => {
                            return (
                                <ObjectCardItem
                                    key={String(item.field)}
                                    label={item.label}
                                    value={data[item.field]}
                                    type={item.type}
                                    relation_field={item.relation_field as string | undefined}
                                    relation={data[item.field]}
                                />
                            );
                        })}

                        {schema.fields.map(({ label, field, type }) => (
                            <ObjectCardItem key={String(field)} label={label} value={data[field]} type={type} />
                        ))}

                    </SimpleGrid>

                </Box>

            </Paper>

            {schema.relations
                ? (Object.entries(schema.relations) as [string, { relation: string; label: string; fields: ViewSchemaFieldItem<any>[] } | undefined][])
                    .map(([relKey, relSchema]) => {
                        const rel = (data as any)[relKey];
                        if (!rel || !relSchema) return null;

                        const rows = relSchema.fields.map((item) => {
                            const value = {
                                label: item.label,
                                type: item.type,
                                value: rel[item.field],
                                field: item.field,
                                relation_field: item.relation_field as string | undefined,
                                relation:  !!item.relation_field ? rel[item.field] : undefined,
                            }
                            return value;
                        });

                        return (
                            <React.Fragment key={relKey}>
                                <Paper bg="white" p="md" shadow="xs">

                                    <Divider
                                        my="0"
                                        mb="xs"
                                        variant="dashed"
                                        labelPosition="left"
                                        label={
                                            <Group gap="3" align="center" justify="flex-start">
                                                <Icon icon={(Icons as any)[relKey] || Icons.point} width={20} />
                                                <Box ml={5}>{relSchema.label}</Box>
                                            </Group>
                                        }
                                        color="var(--mantine-color-gray-4)"
                                        styles={{
                                            label: {
                                                color: 'var(--mantine-color-gray-7)',
                                                fontWeight: 400,
                                                fontSize: '16px',
                                            }
                                        }}
                                    />

                                    <Box p="8px">

                                        <SimpleGrid cols={{ base: 1, md: relationColumns }} spacing="xs">
                                            {rows.map((item,k) => {
                                                
                                                if (item.relation) {
                                                    return (
                                                        <ObjectCardItem
                                                            key={String(item.field)+'-'+String(k)}
                                                            label={item.label}
                                                            value={item.value[item.relation_field as string]}
                                                            type={item.type}
                                                            relation_field={item.relation_field as string | undefined}
                                                            relation={item.relation}
                                                        />
                                                    )
                                                }

                                                return <ObjectCardItem key={String(item.field)} label={item.label} value={item.value} type={item.type} />

                                            })}
                                        </SimpleGrid>

                                    </Box>

                                </Paper>
                            </React.Fragment>
                        );
                    })
                : null}

        </Stack>
    );
}
